import { decodeSubmissionRecord } from './contestDecoders';

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const isString = (value) => typeof value === 'string';
const isInt = (value) => Number.isInteger(value);
const toInt = (value) => (isInt(value) ? value : 0);

function parseJson(value, fallback) {
  if (!isString(value)) return value ?? fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function decodeEach(items, decoder) {
  if (!Array.isArray(items)) return null;
  return items.map(decoder).filter((item) => item !== null);
}

export function decodeUser(data) {
  if (
    !isObject(data) ||
    !isString(data.id) ||
    !isString(data.username) ||
    !isString(data.email) ||
    !isString(data.role) ||
    !isString(data.tenant_id)
  ) {
    return null;
  }
  return { id: data.id, username: data.username, email: data.email, role: data.role, tenantId: data.tenant_id };
}

export function decodeTopicsList(data) {
  return decodeEach(data, (item) =>
    isObject(item) && isString(item.topic) && isInt(item.question_count)
      ? { topic: item.topic, count: item.question_count }
      : null
  );
}

const STATUSES = ['solved', 'attempted', 'unattempted'];

export function decodeProblemsList(data) {
  return decodeEach(data, (item) => {
    if (!isObject(item) || !isInt(item.id) || !isString(item.question_name) || !isString(item.level)) return null;
    return {
      id: item.id,
      title: item.question_name,
      difficulty: item.level,
      submissionCount: toInt(item.submission_count),
      acceptedCount: toInt(item.accepted_count),
      status: STATUSES.includes(item.status) ? item.status : 'unattempted',
      tags: Array.isArray(item.tags) ? item.tags.filter(isString) : [],
    };
  });
}

function decodeDailyEntry(item) {
  if (!isObject(item) || !isInt(item.question_id) || !isString(item.question_name) || !isString(item.date)) return null;
  return {
    questionId: item.question_id,
    title: item.question_name,
    difficulty: isString(item.level) ? item.level : '',
    date: item.date,
    solved: item.solved === true,
    submissionCount: toInt(item.submission_count),
    acceptedCount: toInt(item.accepted_count),
  };
}

export function decodeDailyData(data) {
  if (!isObject(data)) return null;
  return {
    today: data.today ? decodeDailyEntry(data.today) : null,
    calendar: decodeEach(data.calendar ?? [], decodeDailyEntry) ?? [],
    maxStreak: toInt(data.max_streak),
  };
}

export function decodeProblemDetail(data) {
  if (!isObject(data) || !isInt(data.id) || !isString(data.question_name) || !isString(data.level)) return null;
  const sourceCode = parseJson(data.source_code, {});
  const samples = parseJson(data.sample_tests, []);
  return {
    id: data.id,
    title: data.question_name,
    difficulty: data.level,
    description: parseJson(data.description, {}) ?? {},
    sourceCode: isObject(sourceCode) ? sourceCode : {},
    sampleTests: Array.isArray(samples)
      ? samples.map((sample) => ({
          input: isString(sample?.input) ? sample.input : '',
          output: isString(sample?.output) ? sample.output : '',
        }))
      : [],
    solved: data.status === 'solved',
    submissionCount: toInt(data.submission_count),
    acceptedCount: toInt(data.accepted_count),
  };
}

export function decodeTags(data) {
  return Array.isArray(data) ? data.filter(isString) : null;
}

export function decodeProblemSubmissions(data) {
  const submissions = decodeEach(data, (row) =>
    decodeSubmissionRecord(isObject(row) ? { ...row, verdict: isString(row.verdict) ? row.verdict : 'PENDING' } : row)
  );
  return submissions === null ? null : { submissions };
}

export function decodeProfileStats(data) {
  if (!isObject(data)) return null;
  return {
    Easy: toInt(data.easy_solved_count),
    Medium: toInt(data.medium_solved_count),
    Hard: toInt(data.hard_solved_count),
  };
}

export function decodeQuestionCounts(data) {
  if (!isObject(data)) return null;
  return { Easy: toInt(data.easy), Medium: toInt(data.medium), Hard: toInt(data.hard), total: toInt(data.count) };
}

export function decodeUserSubmissions(data) {
  return decodeEach(data, (row) => {
    if (!isObject(row) || !isString(row.id) || !isString(row.language)) return null;
    const createdAt = row.submitted_at ?? row.created_at;
    const executionTime = row.executionTime === null || row.executionTime === undefined ? null : Number(row.executionTime);
    return {
      id: row.id,
      verdict: isString(row.verdict) ? row.verdict : 'PENDING',
      language: row.language,
      title: isString(row.question_name) ? row.question_name : '',
      questionId: isInt(row.questionId) ? row.questionId : null,
      createdAt: isString(createdAt) ? createdAt : null,
      executionTime: Number.isFinite(executionTime) ? executionTime : null,
      memoryUsed: isInt(row.memoryUsed) ? row.memoryUsed : null,
    };
  });
}
